import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { paymentTypeOptions } from '../data/payPeriods';
import type { PayPeriod, PaymentType } from '../types/payment';
import { formatCurrency } from '../utils/currency';
interface SuccessStateProps {
  paymentType: PaymentType;
  amount: number;
  payPeriod: PayPeriod;
  savedCount: number;
  onAddAnother: () => void;
  onDone: () => void;
}
export function SuccessState({
  paymentType,
  amount,
  payPeriod,
  savedCount,
  onAddAnother,
  onDone
}: SuccessStateProps) {
  const typeLabel = paymentTypeOptions.find(option => option.id === paymentType)?.label ?? 'Payment';
  return <div className="px-6 py-10 text-center" role="status" aria-live="polite" data-id="element-211" data-mp="d24y9ko9e8c9">
      <motion.span initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.28,
      ease: [0.16, 1, 0.3, 1]
    }} className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-50 text-success-600 ring-8 ring-success-50/60" data-id="element-212" data-mp="2cf90llk54ti7">
        <CheckIcon className="h-7 w-7" aria-hidden="true" strokeWidth={2.5} data-id="element-213" data-mp="ay5o8oqxxjfz" />
      </motion.span>

      <h2 className="mt-5 text-lg font-semibold text-ink-900" data-id="element-214" data-mp="3t4al5pm56wkt">Payment added</h2>
      <p className="mt-1 text-sm text-ink-500" data-id="element-215" data-mp="1vwei13hxu3n">
        Added to {payPeriod.name.split(' · ')[0]} for {payPeriod.employeeName}.
      </p>

      <div className="mx-auto mt-6 max-w-sm rounded-lg border border-ink-200 bg-white shadow-card" data-id="element-216" data-mp="pn259baz66sq">
        <div className="flex items-center justify-between px-4 py-3" data-id="element-217" data-mp="ukk4g9z6x7nj">
          <span className="text-xs font-medium text-ink-500" data-id="element-218" data-mp="fvfnsx81nbhj">Payment Type</span>
          <span className="text-sm font-semibold text-ink-900" data-id="element-219" data-mp="kv0iuwk3ezoz">{typeLabel}</span>
        </div>
        <div className="flex items-center justify-between border-t border-ink-100 px-4 py-3" data-id="element-220" data-mp="18kmjlog45mz3">
          <span className="text-xs font-medium text-ink-500" data-id="element-221" data-mp="1dbl47084x785">Amount</span>
          <span className="numeric text-base font-semibold text-ink-900" data-id="element-222" data-mp="1ddo2e7jfy6ny">
            {formatCurrency(amount)}
          </span>
        </div>
      </div>

      {savedCount > 1 && <p className="mt-3 text-xs text-ink-400" data-id="element-223" data-mp="1i9jo2nefbxx2">
          {savedCount} payment line items added in this session.
        </p>}

      <div className="mt-7 flex flex-col-reverse items-center justify-center gap-2 sm:flex-row" data-id="element-224" data-mp="198c47acnmzmi">
        <button type="button" onClick={onAddAnother} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 focus:outline-none focus:ring-4 focus:ring-ink-100 sm:w-auto" data-id="element-225" data-mp="148pew2jjxsxc">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-226" data-mp="pl4ckaui3xqh" />
          Add Another Payment
        </button>
        <button type="button" onClick={onDone} className="w-full rounded-md bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-card transition hover:bg-brand-700 focus:outline-none focus:ring-4 focus:ring-brand-200 sm:w-auto" data-id="element-227" data-mp="lh6f04ktquzj">
          Done
        </button>
      </div>
    </div>;
}